import type { MetaDescriptor, Params } from 'react-router';

import { generateCacheControlV2 } from '@boc/framework/headers/cache';
import { Duration } from '@boc/duration';
import { generateShortDescription } from '@boc/framework/products/description';

import { genHrefLangs } from '@boc/framework';

import { loader } from '~/local/products/details/loader';
import { ProductPage } from '~/local/products/details/page';
import { canonicalize, parseParams, urls } from '~/urls';

import { brandInfo } from '#/brand';
import { DEFAULT_CHANNEL, channels } from '#/channels';
import { DEFAULT_LOCALE, supportedLocales } from '#/locales';
import { genProductTitle } from '#/products/seo';

import type { Route } from './+types/details';

export const handle = {
  header: {
    bgColour: 'bg-primary-2-10',
  },
  bgColour: 'bg-primary-2-10',
};

export { loader };

export const meta: Route.MetaFunction = ({ data, params, location }) => {
  if (!data?.product) return [];

  const { channel } = parseParams(params);

  const canonical: MetaDescriptor = {
    tagName: 'link',
    rel: 'canonical',
    href: canonicalize(location),
  };

  const hreflangs = genHrefLangs({
    location,
    canonicalize,
    url: (params: Params) => urls.products.details(params, { slug: data.product.slug }),
    channels: Object.values(channels),
    defaultChannel: DEFAULT_CHANNEL,
    defaultLocale: DEFAULT_LOCALE,
    supportedLocales,
  });

  // Extract variant from URL params for OG image
  const searchParams = new URL(`https://example.com${location.pathname}${location.search}`)
    .searchParams;
  const variantId = searchParams.get('variant');

  // Find selected variant or use default
  const selectedVariant = variantId
    ? data.product.variants?.find((v) => v.id === variantId)
    : null;
  const variant = selectedVariant || data.product.defaultVariant || data.product.variants?.[0];

  // Use variant media, or fall back to product thumbnail
  const ogImage = variant?.media?.[0]?.url || data.product.thumbnail?.url;

  return [
    canonical,
    ...hreflangs,
    { name: 'og:image', content: ogImage },

    {
      title: channel.seo?.title
        ? `Buy ${channel.seo.title(genProductTitle(data.product))} only at ${brandInfo.name}`
        : `Buy ${genProductTitle(data.product)} only at ${brandInfo.name}`,
    },
    {
      name: 'og:title',
      content: channel.seo?.ogTitle
        ? channel.seo.ogTitle(genProductTitle(data.product))
        : genProductTitle(data.product),
    },

    {
      name: 'description',
      content: channel.seo?.description
        ? channel.seo.description(generateShortDescription(data.product))
        : generateShortDescription(data.product),
    },
    {
      name: 'og:description',
      content: channel.seo?.description
        ? channel.seo.description(generateShortDescription(data.product))
        : generateShortDescription(data.product),
    },
  ];
};

export const headers: Route.HeadersFunction = ({ loaderHeaders }) => {
  const loaderCacheHeader = loaderHeaders.get('Cache-Control');

  if (loaderCacheHeader) {
    return { 'Cache-Control': loaderCacheHeader };
  }

  return {
    'Cache-Control': generateCacheControlV2({
      maxAge: Duration.minutes(1),
      sharedMaxAge: Duration.minutes(5),
    }),
  };
};

export default ProductPage;
